import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Badge,
  Box,
  Button,
  Center,
  Code,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaPencil, FaTrash } from "react-icons/fa6";
import { CInput, CTextarea } from "../../components";
import {
  EXTERNAL_INTEGRATION_ENDPOINT_HTTP_METHODS,
  EXTERNAL_INTEGRATION_ENDPOINT_TYPES,
  EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER,
  EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS,
} from "../../constants";
import { ExternalIntegrationModel } from "../../services";
import { externalIntegrationSchema } from "../../utils/schemas";
import { normalizeExternalIntegrationList } from "../../utils/externalIntegration";

const emptyForm = {
  name: "",
  type: "",
  url: "",
  headersJson: "",
};

function integrationId(row) {
  return row?._id ?? row?.id ?? "";
}

function listRowKey(row, index) {
  const base = integrationId(row) || `row-${index}`;
  if (row.__legacyEndpointIndex != null) {
    return `${base}-${row.__legacyEndpointIndex}`;
  }
  return `${base}-${index}`;
}

function truncateUrl(url, max = 48) {
  if (!url) return "—";
  return url.length <= max ? url : `${url.slice(0, max)}…`;
}

function endpointLabel(type) {
  return EXTERNAL_INTEGRATION_ENDPOINT_TYPES[type] || type || "—";
}

function httpMethodForType(type) {
  return EXTERNAL_INTEGRATION_ENDPOINT_HTTP_METHODS[type] || "POST";
}

function HttpMethodBadge({ method }) {
  if (!method) return null;
  return (
    <Badge
      colorScheme="blue"
      variant="solid"
      fontFamily="mono"
      fontSize="10px"
      px={2}
      py={0.5}
      textTransform="uppercase"
      letterSpacing="wider"
      flexShrink={0}
    >
      {method}
    </Badge>
  );
}

function rowToFormValues(row) {
  return {
    name: row.name ?? "",
    type: row.type ?? "",
    url: row.url ?? "",
    headersJson:
      row.headers && Object.keys(row.headers).length > 0
        ? JSON.stringify(row.headers, null, 2)
        : "",
  };
}

function formValuesToApiBody(values) {
  return {
    name: values.name.trim(),
    type: values.type,
    url: values.url.trim(),
    headers: values.headersJson?.trim()
      ? JSON.parse(values.headersJson.trim())
      : {},
  };
}

export default function ExternalIntegrations() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const modal = useDisclosure();
  const deleteModal = useDisclosure();

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ExternalIntegrationModel.get();
      const raw = res.data?.data ?? res.data;
      setItems(
        normalizeExternalIntegrationList(Array.isArray(raw) ? raw : [])
      );
    } catch {
      toast({
        title: "Could not load integrations",
        description:
          "If this persists, the external-integrations API may not be deployed yet.",
        status: "error",
        isClosable: true,
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const formik = useFormik({
    initialValues: emptyForm,
    validationSchema: externalIntegrationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const body = formValuesToApiBody(values);

        if (editing) {
          await ExternalIntegrationModel.update(integrationId(editing), body);
          toast({
            description: "Integration updated.",
            status: "success",
            isClosable: true,
          });
        } else {
          await ExternalIntegrationModel.create(body);
          toast({
            description: "Integration added.",
            status: "success",
            isClosable: true,
          });
        }
        resetForm();
        setEditing(null);
        modal.onClose();
        loadItems();
      } catch {
        toast({
          description: "Something went wrong. Please try again.",
          status: "error",
          isClosable: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const openCreate = () => {
    setEditing(null);
    formik.resetForm({ values: { ...emptyForm } });
    modal.onOpen();
  };

  const openEdit = (row) => {
    setEditing(row);
    formik.resetForm({ values: rowToFormValues(row) });
    modal.onOpen();
  };

  const closeModal = () => {
    setEditing(null);
    formik.resetForm({ values: { ...emptyForm } });
    modal.onClose();
  };

  const confirmDelete = (row) => {
    setDeleting(row);
    deleteModal.onOpen();
  };

  const runDelete = async () => {
    if (!deleting) return;
    try {
      await ExternalIntegrationModel.delete(integrationId(deleting));
      toast({
        description: "Integration removed.",
        status: "success",
        isClosable: true,
      });
      deleteModal.onClose();
      setDeleting(null);
      loadItems();
    } catch {
      toast({
        description: "Could not delete integration.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const isFetch = formik.values.type === "fetch_order_by_id";

  return (
    <>
      <HStack
        justifyContent="space-between"
        alignItems={{ base: "start", md: "center" }}
        flexDirection={{ base: "column", md: "row" }}
        mb={4}
        gap={3}
      >
        <Heading fontSize={{ base: "xl", md: "3xl" }}>
          External API integrations
        </Heading>
        <Button
          bgColor="black"
          borderRadius="lg"
          fontWeight="medium"
          py={6}
          px={7}
          onClick={openCreate}
        >
          Add integration
        </Button>
      </HStack>

      <VStack align="stretch" spacing={2} mb={6} maxW="3xl">
        <Text color="gray.600" fontSize="sm">
          Each integration is <strong>one</strong> URL: choose the operation,
          paste the URL, and optional JSON headers for that call only.
        </Text>
        <Box fontSize="sm" color="gray.700" pl={1}>
          <Text mb={1}>
            <HttpMethodBadge method="GET" />{" "}
            <strong>Fetch order</strong> — URL must include{" "}
            <Code fontSize="xs">{EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER}</Code>
            . We substitute the real order id (no body).
          </Text>
          <Text>
            <HttpMethodBadge method="POST" />{" "}
            <strong>Upload PDF result</strong> —{" "}
            <Code fontSize="xs">multipart/form-data</Code> with parts{" "}
            <Code fontSize="xs">
              {EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.orderId}
            </Code>{" "}
            (order id) and{" "}
            <Code fontSize="xs">
              {EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.resultPdf}
            </Code>{" "}
            (PDF file). Optional headers apply; do not override{" "}
            <Code fontSize="xs">Content-Type</Code> for this call.
          </Text>
        </Box>
      </VStack>

      {loading ? (
        <Center py={12}>
          <Spinner />
        </Center>
      ) : items.length === 0 ? (
        <Box
          borderWidth="1px"
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg="gray.50"
        >
          <Text color="gray.600" mb={4}>
            No integrations yet.
          </Text>
          <Button
            bgColor="black"
            borderRadius="lg"
            fontWeight="medium"
            py={6}
            px={7}
            onClick={openCreate}
          >
            Add integration
          </Button>
        </Box>
      ) : (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Operation</Th>
                <Th>URL</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((row, idx) => (
                <Tr key={listRowKey(row, idx)}>
                  <Td fontWeight="medium" verticalAlign="top">
                    {row.name}
                    {row.__legacyEndpointIndex != null ? (
                      <Text fontSize="xs" color="gray.500">
                        Legacy bundle row
                      </Text>
                    ) : null}
                  </Td>
                  <Td verticalAlign="top">
                    <HStack spacing={2} flexWrap="wrap">
                      <HttpMethodBadge method={httpMethodForType(row.type)} />
                      <Badge colorScheme="orange" variant="subtle">
                        {endpointLabel(row.type)}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td verticalAlign="top" maxW="280px">
                    <Text fontSize="sm" color="gray.600" noOfLines={3} title={row.url}>
                      {truncateUrl(row.url, 96)}
                    </Text>
                  </Td>
                  <Td textAlign="right" whiteSpace="nowrap" verticalAlign="top">
                    <IconButton
                      aria-label="Edit integration"
                      icon={<FaPencil />}
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(row)}
                    />
                    <IconButton
                      aria-label="Delete integration"
                      icon={<FaTrash />}
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                      onClick={() => confirmDelete(row)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Modal isOpen={modal.isOpen} onClose={closeModal} size="xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            {editing ? "Edit integration" : "Add integration"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <CInput
                label="Name"
                name="name"
                placeholder="e.g. Acme — fetch orders"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors.name}
                touched={formik.touched.name}
              />

              <FormControl
                isInvalid={
                  !!(formik.errors.type && formik.touched.type)
                }
              >
                <FormLabel fontWeight="semibold">Operation</FormLabel>
                <Select
                  name="type"
                  placeholder="Select operation…"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  bg="white"
                  borderRadius="lg"
                >
                  {Object.entries(EXTERNAL_INTEGRATION_ENDPOINT_TYPES).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {httpMethodForType(value)} — {label}
                      </option>
                    )
                  )}
                </Select>
                {formik.errors.type && formik.touched.type && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {formik.errors.type}
                  </Text>
                )}
              </FormControl>

              <CInput
                label={
                  isFetch
                    ? `URL (must contain ${EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER})`
                    : "URL"
                }
                name="url"
                placeholder={
                  isFetch
                    ? `https://partner.example.com/v1/orders/${EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER}`
                    : "https://api.example.com/results/upload"
                }
                value={formik.values.url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors.url}
                touched={formik.touched.url}
              />

              {isFetch ? (
                <Text fontSize="xs" color="gray.600">
                  Example:{" "}
                  <Code fontSize="xs">
                    {`https://partner.example.com/v1/orders/${EXTERNAL_INTEGRATION_ORDER_ID_PLACEHOLDER}`}
                  </Code>
                </Text>
              ) : formik.values.type === "upload_pdf_result" ? (
                <Text fontSize="xs" color="gray.600">
                  We call this URL with <strong>POST</strong> and{" "}
                  <Code fontSize="xs">multipart/form-data</Code>: part{" "}
                  <Code fontSize="xs">
                    {EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.orderId}
                  </Code>{" "}
                  (text order id) and part{" "}
                  <Code fontSize="xs">
                    {EXTERNAL_INTEGRATION_SUBMIT_FORM_FIELDS.resultPdf}
                  </Code>{" "}
                  (the PDF file). Use headers below only for auth etc.—not{" "}
                  <Code fontSize="xs">Content-Type</Code>.
                </Text>
              ) : null}

              <CTextarea
                label="Headers (optional JSON object)"
                name="headersJson"
                placeholder='{ "Authorization": "Bearer …" }'
                value={formik.values.headersJson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors.headersJson}
                touched={formik.touched.headersJson}
                rows={4}
              />
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              bgColor="black"
              borderRadius="lg"
              fontWeight="medium"
              py={6}
              px={7}
              onClick={formik.handleSubmit}
              isLoading={formik.isSubmitting}
              loadingText="Saving…"
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove integration?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              This will remove{" "}
              <strong>{deleting?.name || "this integration"}</strong>
              {deleting?.type ? (
                <>
                  {" "}
                  (<Code fontSize="sm">{endpointLabel(deleting.type)}</Code>)
                </>
              ) : null}
              .
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={deleteModal.onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={runDelete}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
